import Head from 'next/head'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { FormEventHandler, useCallback, useEffect, useMemo, useState } from 'react'

import Carousel from '@/components/Carousel'
import LoadingIcon from '@/components/icons/Loading'
import Layout from '@/components/Layout'
import Popup from '@/components/Popup'
import AddToCartConfirmation from '@/components/popups/AddToCartConfirmation'
import RelatedProduct from '@/components/Product'
import Description from '@/components/product/Description'
import RatingStars from '@/components/product/RatingStars'
import { useAuthentication } from '@/components/providers/AuthenticationProvider'
import { useCart } from '@/components/providers/CartProvider'
import { useDependencies } from '@/components/providers/DependencyProvider'
import { useFavorites } from '@/components/providers/FavoritesProvider'
import useMoneyFormatter from '@/hooks/useMoneyFormatter'
import useProduct from '@/hooks/useProduct'
import useQuantity from '@/hooks/useQuantity'
import useRelatedProducts from '@/hooks/useRelatedProducts'
import { Status, useStatus } from '@/hooks/useStatus'
import { calculateUrl } from '@/utils/search'
import { transform } from '@/utils/string'

// TODO: Change heart svgs

function ReviewPopup({ product, close }: {product: BigCommerceProduct, close: () => void}): JSX.Element {
	const { apiFetcher } = useDependencies()
	const [rating, setRating] = useState(0)
	const [status, setStatus] = useStatus(Status.Idle)
	const [title, setTitle] = useState('')
	const [text, setText] = useState('')
	const [noRating, setNoRating] = useState(false)

	const handleSubmit: FormEventHandler<HTMLFormElement> = event => {
		event.preventDefault()

		if (status === Status.Success || status === Status.Loading) {
			return
		}

		if (rating < 1) {
			setNoRating(true)
			return
		}

		setStatus(Status.Loading)

		apiFetcher(`/reviews/${product.id}`, {
			method: 'POST',
			body: JSON.stringify({
				rating,
				title,
				text,
			}),
		}).then(() => {
			setNoRating(false)
			setStatus(Status.Success)
		}).catch(() => {
			setNoRating(false)
			setStatus(Status.Error)
		})
	}

	return (status === Status.Success ? (
		<div className="absolute z-20 inset-0">
			<div className="relative bg-white shadow-md rounded w-full max-w-lg p-4 sm:flex sm:justify-between sm:items-center sm:rounded-md lg:rounded-lg lg:max-w-xl lg:px-6 lg:py-5 xl:px-9 xl:pt-6 xl:pb-9">
				<div className="text-center sm:text-left">
					<h3 className="text-xl font-bold md:text-2xl xl:text-3xl">Your review has been sumbitted!</h3>
					<p className="mt-2 text-sm md:text-base">Thank you for taking the time to leave a rating. Your review is currently pending approval.</p>
				</div>

				<svg className="hidden w-12 h-10 sm:block sm:ml-8" viewBox="0 0 48 42" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path className="text-green-400" d="M16.3245 34.148L43.5264 1.36361C44.4399 0.247118 46.0639 0.145618 47.0789 1.05912C48.1954 1.97261 48.2969 3.59661 47.3834 4.61161L17.5425 40.644C17.5425 40.644 16.832 41.5575 15.614 41.5575C14.396 41.5575 13.4825 40.644 13.4825 40.644L0.795013 27.043C-0.118485 26.028 -0.321484 24.5056 0.592013 23.4906C1.60701 22.3741 3.33251 22.3741 4.3475 23.3891L14.396 34.0465C14.396 34.0465 14.802 34.554 15.411 34.554C16.02 34.554 16.3245 34.148 16.3245 34.148Z" fill="currentColor"/>
				</svg>
			</div>
		</div>
	) : (
		<>
			<div className="absolute z-20 inset-0">
				<div className="relative bg-white shadow-md rounded w-full max-w-lg p-4 sm:rounded-md lg:rounded-lg lg:max-w-xl lg:px-6 lg:py-5 xl:px-9 xl:pt-6 xl:pb-9">
					<button onClick={close} className="absolute top-0 right-0 mt-4 mr-4 h-3 w-3">
						<svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14">
							<path className="text-gray-800" d="M13.129 11.828l-5.06-5.06 5.05-5.05A.932.932 0 0013.117.39.932.932 0 0011.79.388L6.765 5.413 1.705.353C1.32.02.733-.006.35.377a.932.932 0 00.002 1.327L5.387 6.74.363 11.764a.932.932 0 000 1.328.932.932 0 001.329.001l5.024-5.025 5.035 5.035a.932.932 0 001.328.002c.357-.357.408-.92.05-1.277z" fill="currentColor"/>
						</svg>
					</button>

					<p className="text-center text-lg font-bold sm:text-2xl xl:text-3xl">Review this item</p>

					<div className="mt-2 border-t border-gray-200 pt-2 sm:pt-4 sm:mt-4">
						<p className="font-bold sm:text-xl xl:text-2xl">{product.name}</p>
						<p className="text-xs font-light sm:text-sm">SKU: {product.sku}</p>

						<form onSubmit={handleSubmit}>
							<div className="mt-4 xl:mt-6">
								<p className="text-sm font-bold sm:text-base">Rating</p>
								<RatingStars rating={rating} setRating={setRating}/>
							</div>

							<div className="mt-3 sm:mt-4 xl:mt-5">
								<label className="text-sm font-bold sm:text-base" htmlFor="title">Review Headline</label>
								<input autoComplete="off" value={title} onChange={event => setTitle(event.target.value)} placeholder="Subject" required className="h-8 w-full shadow-input text-sm appearance-none rounded mt-1.5 px-2 py-1.5 sm:text-base md:h-11" type="text" name="title" id="title" />
							</div>

							<div className="mt-3 sm:mt-4 xl:mt-5">
								<label className="text-sm font-bold sm:text-base" htmlFor="review">Review</label>
								<textarea autoComplete="off" value={text} onChange={event => setText(event.target.value)} placeholder="Comments" required className="w-full shadow-input text-sm appearance-none rounded mt-1.5 px-2 py-1.5 sm:text-base h-24 sm:h-32" name="review" id="review" />
							</div>

							{noRating && <p className="text-red-700 text-center mt-4 text-sm font-bold sm:text-base">Please select a rating above.</p>}
							{status === Status.Error && <p className="text-red-700 text-center mt-4 text-sm font-bold sm:text-base">Unexpected error. Please try again later.</p>}

							<div className="mt-4 flex justify-center items-center md:justify-start xl:mt-6">
								<button type="submit" className="px-5 py-1.5 text-sm font-extrabold bg-gradient-to-b from-green-400 via-green-500 to-green-500 text-white rounded-full uppercase shadow-md leading-5 md:px-4 md:py-2 md:text-base md:leading-5">{status === Status.Loading ? 'Submitting' : 'Submit Review'}</button>
							</div>
						</form>
					</div>
				</div>
			</div>
		</>
	))
}

function ProductImages({ product }: { product: BigCommerceProduct }): JSX.Element {
	const mobileImages = product.images.slice()
	const [selectedImage, setSelectedImage] = useState(0)
	const [page, setPage] = useState(0)
	const favorites = useFavorites()

	if (mobileImages.length === 0) {
		mobileImages.push({
			url: '/img/Image-Coming-Soon.jpg',
			altText: 'Image Coming Soon',
		})
	}

	const isFavorited = favorites.isFavorited(product)

	const desktopImages = mobileImages.slice(page * 5, (page * 5) + 5)
	const mobileImage = mobileImages[selectedImage]
	const desktopImage = desktopImages[selectedImage]

	const hasNextPage = page < Math.floor(mobileImages.length / 5)
	const hasPrevPage = page > 0

	const prevPage = useCallback(() => {
		if (hasPrevPage) {
			setPage(page => page - 1)
			setSelectedImage(0)
		}
	}, [hasPrevPage, setPage, setSelectedImage])

	const nextPage = useCallback(() => {
		if (hasNextPage) {
			setPage(page => page + 1)
			setSelectedImage(0)
		}
	}, [hasNextPage, setPage, setSelectedImage])

	return (
		<div className="w-full">
			<div className="lg:hidden">
				<div className="relative mx-auto h-72 w-72 md:h-80 md:w-80" key={mobileImage.url}>
					{ favorites.hasFavorites && isFavorited !== undefined && (
						<div className="absolute top-0 right-0 mt-4 mr-4">
							{isFavorited ? (
								<button onClick={async () => favorites.removeFavorite(product)}>
									<svg fill="none" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
										<rect x=".5" y=".5" width="23" height="23" rx="11.5" fill="#e1e3e5" stroke="#E1E3E5"/>
										<path d="M11.594 8.887l.398.534.402-.531a3.524 3.524 0 012.821-1.39c1.935 0 3.285 1.33 3.285 3.163 0 .667-.286 1.418-.792 2.216-.502.793-1.194 1.59-1.941 2.336-1.112 1.111-2.292 2.065-3.11 2.725-.26.21-.485.392-.657.538a40.641 40.641 0 00-.618-.503c-.818-.659-2.02-1.627-3.149-2.756-.747-.747-1.439-1.544-1.941-2.338-.506-.8-.792-1.551-.792-2.218C5.5 8.83 6.85 7.5 8.785 7.5a3.49 3.49 0 012.809 1.387z" fill="#737a86" stroke="#737a86"/>
									</svg>
								</button>
							) : (
								<button onClick={async () => favorites.addFavorite(product)}>
									<svg fill="none" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
										<rect x=".5" y=".5" width="23" height="23" rx="11.5" stroke="#E1E3E5"/>
										<path d="M11.594 8.887l.398.534.402-.531a3.524 3.524 0 012.821-1.39c1.935 0 3.285 1.33 3.285 3.163 0 .667-.286 1.418-.792 2.216-.502.793-1.194 1.59-1.941 2.336-1.112 1.111-2.292 2.065-3.11 2.725-.26.21-.485.392-.657.538a40.641 40.641 0 00-.618-.503c-.818-.659-2.02-1.627-3.149-2.756-.747-.747-1.439-1.544-1.941-2.338-.506-.8-.792-1.551-.792-2.218C5.5 8.83 6.85 7.5 8.785 7.5a3.49 3.49 0 012.809 1.387z" stroke="#363940"/>
									</svg>
								</button>
							)}
						</div>
					)}
					<img className="w-full h-full object-contain object-center" alt={mobileImage.altText} src={mobileImage.url} />
				</div>

				{ mobileImages.length > 1 && (
					<div className="mt-2 flex justify-center space-x-1">
						{ mobileImages.map((image, index) => (
							<button className="p-2 rounded-full ring-offset-0" key={image.url} onClick={() => setSelectedImage(index)}>
								<div className="border-gray-800 border rounded-full p-0.5">
									<div className={`h-2 w-2 rounded-full${selectedImage === index ? ' bg-gray-800' : 'bg-transparent'}`} />
								</div>
							</button>
						))}
					</div>
				)}
			</div>

			<div className="hidden lg:flex">
				<div className="flex flex-col space-y-3">
					{desktopImages.map((image, index) => (
						<button key={index} onClick={() => setSelectedImage(index)}>
							<div className={`border p-0.5 ${selectedImage === index ? 'border-gray-800' : 'border-gray-200'}`}>
								<img className="h-12 w-12 object-contain object-center" alt={image.altText} src={image.url} />
							</div>
						</button>
					))}

					{mobileImages.length > 5 && (
						<div className="!mt-auto flex justify-center">
							<button onClick={prevPage} className={`rounded-full border border-gray-200 from-white via-gray-100 to-gray-300 p-0.5 ring-offset-gray-100 ${hasPrevPage ? 'bg-gradient-to-b' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`} disabled={!(hasPrevPage)}>
								<svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
									<path d="M9.24115 12.5839L14.4203 17.763C14.5726 17.9154 14.7757 18 14.9957 18C15.2158 18 15.4189 17.9154 15.5712 17.763C15.8928 17.4415 15.8928 16.9337 15.5712 16.6121L11.0691 12.11C11.0014 12.0423 11.0014 11.9577 11.0691 11.89L15.5712 7.38787C15.8928 7.06629 15.8928 6.55853 15.5712 6.23695C15.4189 6.08463 15.2158 6 14.9957 6C14.7757 6 14.5726 6.08463 14.4203 6.23695L9.24115 11.4161C8.91957 11.7377 8.91957 12.2623 9.24115 12.5839Z" />
								</svg>
							</button>

							<button onClick={nextPage} className={`rounded-full border border-gray-200 from-white via-gray-100 to-gray-300 p-0.5 ring-offset-gray-100 ${hasNextPage ? 'bg-gradient-to-b' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`} disabled={!(hasNextPage)}>
								<svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
									<path d="M15.5712 11.4161L10.3921 6.23695C10.2398 6.08463 10.0367 6 9.81664 6C9.59662 6 9.39351 6.08463 9.24118 6.23695C8.9196 6.55853 8.9196 7.06629 9.24118 7.38787L13.7433 11.89C13.811 11.9577 13.811 12.0423 13.7433 12.11L9.24118 16.6121C8.9196 16.9337 8.9196 17.4415 9.24118 17.763C9.39351 17.9154 9.59661 18 9.81664 18C10.0367 18 10.2398 17.9154 10.3921 17.763L15.5712 12.5839C15.8928 12.2623 15.8928 11.7377 15.5712 11.4161Z" />
								</svg>
							</button>
						</div>
					)}
				</div>

				<div className="relative mx-auto h-96 w-96">
					{ favorites.hasFavorites && isFavorited !== undefined && (
						<div className="absolute top-0 right-0 mt-4 mr-4">
							{isFavorited ? (
								<button onClick={async () => favorites.removeFavorite(product)}>
									<svg fill="none" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
										<rect x=".5" y=".5" width="23" height="23" rx="11.5" fill="#e1e3e5" stroke="#E1E3E5"/>
										<path d="M11.594 8.887l.398.534.402-.531a3.524 3.524 0 012.821-1.39c1.935 0 3.285 1.33 3.285 3.163 0 .667-.286 1.418-.792 2.216-.502.793-1.194 1.59-1.941 2.336-1.112 1.111-2.292 2.065-3.11 2.725-.26.21-.485.392-.657.538a40.641 40.641 0 00-.618-.503c-.818-.659-2.02-1.627-3.149-2.756-.747-.747-1.439-1.544-1.941-2.338-.506-.8-.792-1.551-.792-2.218C5.5 8.83 6.85 7.5 8.785 7.5a3.49 3.49 0 012.809 1.387z" fill="#737a86" stroke="#737a86"/>
									</svg>
								</button>
							) : (
								<button onClick={async () => favorites.addFavorite(product)}>
									<svg fill="none" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
										<rect x=".5" y=".5" width="23" height="23" rx="11.5" stroke="#E1E3E5"/>
										<path d="M11.594 8.887l.398.534.402-.531a3.524 3.524 0 012.821-1.39c1.935 0 3.285 1.33 3.285 3.163 0 .667-.286 1.418-.792 2.216-.502.793-1.194 1.59-1.941 2.336-1.112 1.111-2.292 2.065-3.11 2.725-.26.21-.485.392-.657.538a40.641 40.641 0 00-.618-.503c-.818-.659-2.02-1.627-3.149-2.756-.747-.747-1.439-1.544-1.941-2.338-.506-.8-.792-1.551-.792-2.218C5.5 8.83 6.85 7.5 8.785 7.5a3.49 3.49 0 012.809 1.387z" stroke="#363940"/>
									</svg>
								</button>
							)}
						</div>
					)}
					<img className="w-full h-full object-contain" alt={desktopImage.altText} src={desktopImage.url} />
				</div>
			</div>
		</div>
	)
}

function ProductPreOrderMessage({ availability }: { availability: ProductPreOrder }): JSX.Element {
	const formatMessage = (date: Date) => (
		<>
			Expected arrival date <span className="text-green-400 font-bold">{date.toLocaleDateString()}</span>
		</>
	)

	return (
		<>
			<div className="flex items-center mt-3.5 -mb-2 lg:mt-6 lg:-mb-8">
				<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 flex-shrink-0 text-green-400 md:h-8 md:w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
				</svg>

				<div className="ml-1 md:ml-2">
					<p className="md:text-lg">{availability.availabilityDate === null ? 'New Inventory Arriving Soon' : formatMessage(availability.availabilityDate)}</p>
				</div>
			</div>
		</>
	)
}

function ProductUnavailableMessage({ availability }: { availability: ProductUnavailable }): JSX.Element {
	return (
		<div className="mt-3.5 -mb-2 lg:mt-6 lg:-mb-8">
			<div className="flex items-center">
				<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 flex-shrink-0 text-green-400 md:h-8 md:w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
				</svg>

				<div className="ml-1 md:ml-2">
					<p className="md:text-lg"><span className="font-bold">Low Stock:</span> Call <a href="tel:18004447223" className="font-bold underline text-green-400">1-800-444-7223</a> for availability or <a href="/contact-us" className="font-bold underline text-green-400">email us</a></p>
				</div>
			</div>
			{availability.message !== null && (
				<div className="availability mt-1 ml-10" dangerouslySetInnerHTML={{ __html: availability.message }}></div>
			)}
		</div>
	)
}

function AvailabilityMessage({ availability }: {availability: BigCommerceProduct['availability']}): JSX.Element {
	return (
		<>
			{availability.kind === 'preorder' && <ProductPreOrderMessage availability={availability} />}

			{availability.kind === 'disabled' && <ProductUnavailableMessage availability={availability} />}

			{availability.description !== '' && (
				<div className="mt-3.5 -mb-2 lg:mt-9 lg:-mb-8">
					<div className="availability" dangerouslySetInnerHTML={{ __html: availability.description }}></div>
				</div>
			)}
		</>
	)
}

const transforms: Array<[RegExp, string]> = [
	[/[ /()'"#&%]/gu, '-'],
	[/--+/gu, '-'],
	[/-$/gu, ''],
	[/^-/gu, ''],
]


export default function Product(): JSX.Element {
	const router = useRouter()
	const { addToCart } = useCart()
	const { email } = useAuthentication()
	const pagePath = router.query.product ?? ''
	const sku = Array.isArray(pagePath) ? pagePath[pagePath.length - 1] : pagePath
	const [reviewOpen, setReviewOpen] = useState(false)
	const [isPopupOpen, setIsPopupOpen] = useState(false)

	const { product, loading } = useProduct(sku)
	const { products: relatedProducts, loading: relatedLoading } = useRelatedProducts(sku)

	const slugifiedLink = useMemo(() => {
		if (product !== null) {
			return `${transform(product.name, transforms)}/${encodeURIComponent(sku)}`
		}

		return ''
	}, [product])

	useEffect(() => {
		if (slugifiedLink !== '') {
			void router.replace(
				slugifiedLink,
				undefined,
				{ shallow: true },
			)
		}
	}, [slugifiedLink])

	const handleLeaveARating: React.MouseEventHandler<HTMLButtonElement> = () => {
		if (typeof email !== 'string') {
			void router.push(`/login?back=${router.asPath}`)
			return
		}

		setReviewOpen(true)
	}

	const money = useMoneyFormatter()
	const { quantity, handleQuantityInput, handleQuantityPlus, handleQuantityMinus, handleQuantityBlur } = useQuantity()

	if (loading) {
		return (
			<div className="py-32 flex justify-center items-center col-span-full">
				<LoadingIcon />
			</div>
		)
	}

	if (product === null) {
		return (
			<div className="container flex items-center justify-center py-48 text-center mx-auto px-4 sm:px-8">
				<div>
					<h3 className="font-semibold tracking-wide text-5xl sm:text-8xl lg:text-9xl">Sorry!</h3>
					<p className="mt-4 font-semibold sm:text-lg sm:mt-6 lg:text-3xl lg:mt-12">The product you are trying to view is not currently available.</p>

					<div className="mt-3 sm:flex sm:items-end sm:justify-center lg:mt-6">
						<p className="font-light text-xs sm:text-sm lg:text-lg">If you have any questions, you can call us at:</p>
						<a className="text-green-400 font-bold text-xl mt-1 sm:font-extrabold sm:text-sm sm:ml-2 lg:text-lg" href="tel:+1-800-444-7223">1-800-444-7223</a>
					</div>

					<div className="mt-4 sm:mt-6 sm:flex sm:justify-center sm:items-end sm:-mx-6 md:-mx-12 lg:mt-12 lg:-mx-24">
						<Link href="/">
							<a className="w-full inline-block py-1 text-xs bg-gradient-to-b from-green-400 via-green-500 to-green-500 text-white rounded-full uppercase font-extrabold shadow-md leading-5 sm:text-base sm:py-2 lg:text-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-400">Back To Home</a>
						</Link>

						<button onClick={() => router.back()} className="w-full mt-2 py-1 text-xs bg-gradient-to-b text-green-400 rounded-full border border-gray-200 uppercase font-extrabold shadow-md leading-5 sm:ml-4 sm:text-base sm:py-2 lg:text-xl lg:ml-8 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-400">Previous Page</button>
					</div>
				</div>
			</div>
		)
	}

	const addProductToCart = async () => {
		await addToCart(product, quantity)
			.then(() => setIsPopupOpen(true))
	}

	return (
		<div className="relative">
			<Head>
				<title key="page-title">{`${product.brand === null ? '' : `${product.brand} `}${product.name} - ${product.sku} | Pace International`}</title>
				{slugifiedLink !== '' && <link rel="canonical" href={`https://paceintl.com/p/${slugifiedLink}`} />}
			</Head>

			{isPopupOpen && (
				<Popup close={() => setIsPopupOpen(false)}>
					<AddToCartConfirmation product={{ ...product, quantity, kind: 'bigcommerce' }} close={() => setIsPopupOpen(false)}/>
				</Popup>
			)}

			{reviewOpen && (
				<div aria-hidden="true" onClick={() => setReviewOpen(false)} className="absolute w-full h-full bg-black opacity-25 z-10 cursor-pointer"></div>
			)}

			<div className="container mx-auto py-6 px-4 md:px-8">
				<section className="lg:flex lg:items-start lg:justify-between lg:mt-4 ">
					<div className="flex justify-center items-center lg:w-1/2">
						<ProductImages product={product} />
					</div>

					<div className="mt-4 sm:mt-6 lg:mt-0 lg:w-1/2">
						{product.brand === null ? (
							<div className="h-7"></div>
						) : (
							<Link href={`/search${calculateUrl('', null, [product.brand])}`}>
								<a className="inline-block text-green-400 font-bold underline text-sm sm:text-base md:text-lg xl:text-xl">{product.brand}</a>
							</Link>
						)}
						<h4 className="font-bold text-lg sm:text-2xl md:text-3xl lg:mt-2 xl:text-4xl">{product.name}</h4>
						<p className="text-xs font-light sm:text-sm md:text-base lg:mt-2 xl:text-lg">SKU: {product.sku}</p>

						<div className="flex relative items-center mt-3 lg:mt-5">
							{reviewOpen && <ReviewPopup product={product} close={() => setReviewOpen(false)}/>}

							<RatingStars rating={product.reviewTotal}/>

							<button onClick={handleLeaveARating} className="ml-3 text-base font-light underline lg:text-lg">Leave a rating</button>
						</div>

						<div className="mt-6 lg:mt-12">
							{product.price !== null && (
								<div className="sm:w-1/2 lg:w-full">
									<p className="text-3xl font-extrabold md:text-4xl">{money.format(product.sale_price ?? product.price)}</p>

									{product.sale_price !== null && (
										<div className="flex items-center mt-2">
											<div className="bg-green-200 px-2 py-0.5">
												<p className="text-xs text-green-800 font-black md:text-sm">{(100 * (product.price - product.sale_price) / product.price).toFixed(0)}% OFF</p>
											</div>
											<p className="text-xs ml-1.5 md:text-sm">Was <span className="line-through">{money.format(product.price)}</span></p>
										</div>
									)}
								</div>
							)}

							<AvailabilityMessage availability={product.availability} />

							{product.availability.kind !== 'disabled' && (
								<div className="mt-6 flex justify-between items-center sm:w-1/2 lg:w-full lg:mt-14 lg:justify-start">
									<div className="bg-gray-200 border px-1 rounded-full justify-between items-center inline-flex md:px-2">
										<button onClick={handleQuantityMinus} className="focus:outline-none rounded-l-full">
											<svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
												<path fillRule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
											</svg>
										</button>

										<input value={quantity} onBlur={handleQuantityBlur} onChange={handleQuantityInput} className={`w-12 bg-white py-px mx-1.5 text-lg text-center appearance-none ${quantity.toString().length > 3 ? 'w-20' : ''}`} maxLength={6} type="number"></input>

										<button onClick={handleQuantityPlus} className="focus:outline-none rounded-r-full">
											<svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
												<path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
											</svg>
										</button>
									</div>
									<button onClick={addProductToCart} className="bg-gradient-to-b from-green-400 via-green-500 to-green-500 text-white rounded-full uppercase font-extrabold shadow-md leading-5 text-base py-2 px-3.5 md:py-2.5 md:px-5 md:text-lg lg:ml-14 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-400">{product.availability.kind === 'preorder' ? 'Pre-Order Now' : 'Add To Cart'}</button>
								</div>
							)}
						</div>
					</div>
				</section>

				<section className="mt-12">
					<div>
						<div className="flex justify-between items-center sm:justify-start sm:space-x-12">
							<button className="border-b-4 border-green-500 focus:outline-none pb-1 text-sm font-bold sm:text-lg md:text-xl xl:text-2xl">Description</button>
						</div>
						<div className="bg-gray-200 h-px"></div>
					</div>

					<div className="mt-4 sm:mt-6">
						<Description product={product} />
					</div>
				</section>

				<div className="bg-gray-200 h-px mt-6 -mb-px lg:mt-8 xl:mt-12"></div>

				<section className="mt-6 lg:mt-8 xl:mt-12">
					<div className="w-full px-6 sm:px-0">
						<p className="text-lg font-bold sm:text-xl md:text-2xl">Related Products</p>

						{relatedLoading ? (
							<div className="py-32 flex justify-center items-center col-span-full">
								<LoadingIcon />
							</div>
						) : (
							<div className="mt-6 sm:-mx-2">
								<Carousel options={{
									groupCells: true,
									wrapAround: false,
									pageDots: false,
									cellAlign: 'left',
									contain: true,
								}}>
									{relatedProducts?.map(relatedProduct => (
										<div key={relatedProduct.id} className="w-full max-w-xs px-2 py-2 md:max-w-full md:w-1/2 lg:w-1/3 xl:w-1/4">
											<RelatedProduct product={{ ...relatedProduct, kind: 'simple' }} />
										</div>
									))}
								</Carousel>
							</div>
						)}
					</div>
				</section>
			</div>
		</div>
	)
}

Product.Layout = Layout
